import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function NewDiscussion() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState(params.get("category") || "");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => { if (ready && user === false) navigate("/signin"); }, [ready, user, navigate]);
  useEffect(() => {
    api.get("/categories").then((r) => { setCats(r.data); if (!category && r.data[0]) setCategory(r.data[0].slug); }).catch(() => {});
  }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/threads", { category_slug: category, title, body });
      if (data.status === "pending") {
        setPending(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate(`/community/${data.thread.category_slug}/${data.thread.slug}`);
      }
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12">
      <Link to="/community" className="text-sm text-mutedplum hover:text-teal">← Back to Community</Link>
      {pending ? (
        <div className="mt-6 bg-white rounded-3xl border border-teal/30 shadow-card p-8 text-center" data-testid="nd-pending">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-teal/15 text-teal mx-auto">✓</span>
          <h1 className="mt-4 font-display text-3xl text-plum">Thanks—your discussion is in review</h1>
          <p className="mt-3 text-mutedplum text-lg">
            To keep the Circle safe and kind, new members' first posts are gently reviewed by our moderator
            before going live. You'll see it appear shortly. It didn't fail—promise. 💜
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={`/community/${category}`} className="rounded-full bg-teal px-7 py-3 font-semibold text-white hover:bg-teal-dark transition-colors" data-testid="nd-pending-browse">Browse the category</Link>
            <button onClick={() => { setPending(false); setTitle(""); setBody(""); }} className="rounded-full border-2 border-plum px-7 py-3 font-semibold text-plum hover:bg-plum hover:text-cream transition-colors">Post another</button>
          </div>
        </div>
      ) : (
      <>
      <h1 className="mt-4 font-display text-4xl text-plum">Start a discussion</h1>
      <p className="mt-2 text-mutedplum text-lg">Ask a real question or share what's on your mind. Kind, honest and judgment-free.</p>

      <form onSubmit={submit} className="mt-8 bg-white rounded-3xl border border-rose/30 shadow-card p-6 sm:p-8 space-y-5" data-testid="new-discussion-form">
        <div>
          <label className="block text-sm font-semibold text-plum mb-1.5">Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="nd-category"
            className="w-full rounded-2xl border-2 border-rose/50 bg-cream px-4 py-3 outline-none focus:border-teal transition-colors">
            {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-plum mb-1.5">Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} required minLength={6} maxLength={160}
            placeholder="e.g. How do you stay motivated on low-energy days?" data-testid="nd-title"
            className="w-full rounded-2xl border-2 border-rose/50 bg-cream px-4 py-3 outline-none focus:border-teal transition-colors" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-plum mb-1.5">Your message</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} required rows={6}
            placeholder="Add a little context so others can help…" data-testid="nd-body"
            className="w-full rounded-2xl border-2 border-rose/50 bg-cream px-4 py-3 outline-none focus:border-teal transition-colors resize-none" />
        </div>
        {error && <p className="text-sm text-red-500" data-testid="nd-error">{error}</p>}
        <p className="text-xs text-mutedplum">New members' posts are gently reviewed before going live—this keeps the Circle safe and kind.</p>
        <button type="submit" disabled={loading} data-testid="nd-submit"
          className="rounded-full bg-teal px-8 py-3.5 font-semibold text-white hover:bg-teal-dark transition-colors disabled:opacity-60">
          {loading ? "Posting…" : "Post discussion"}
        </button>
      </form>
      </>
      )}
    </div>
  );
}
